import * as React from 'react';
import {useMergeRefs} from '@floating-ui/react';

import {useFormGroupState, useFormGroupId} from '../FormGroup';
import type {SpringTooling} from '../__SharedTypes';
import {getDefinedValue, renderElement} from '../../utils';
import {CloseCircleIcon} from '../../icons';

import {
  TextInputWrapperView,
  TextInputContentView,
  TextInputPrefixSuffixView,
  TextInputView,
  TextInputClearButtonView,
} from './TextInput.styles';

type TextInputType =
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'file'
  | 'month'
  | 'number'
  | 'password'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

export type TextInputProps = {
  /**
   * Focuses the text input when it is mounted. Use this prop sparingly, as it's generally a bad idea to steal the user's keyboard focus if they're not expecting it. This can cause usability and accessibility issues.
   */
  autoFocus?: boolean;
  /**
   * The default value when no value is provided. Used for uncontrolled components.
   */
  defaultValue?: string;
  /**
   * Renders the disabled state for the `TextInput`, which is visualized by being semi-transparent.
   */
  isDisabled?: boolean;
  /**
   * Renders the error state for the `TextInput`, which is visualized by a red border.
   */
  isError?: boolean;
  /**
   * Indicates whether or not the field is required and is used to make TextInput more accessible to assistive technologies.
   */
  isRequired?: boolean;
  /**
   * The ID for the TextInput.
   */
  id?: string;
  /**
   * The name for the TextInput. Used to identify the input when working with FormData.
   */
  name?: string;
  /**
   * Placeholder content when no value is provided.
   */
  placeholder?: string;
  /**
   * Prefix content can be rendered in front of the input field. This is useful to provide extra context for the
   * input value. Examples may include labels or icons and symbols (i.e. `$`).
   */
  prefix?: React.ReactNode | React.ComponentType;
  /**
   * Renders control in read-only mode.
   */
  isReadOnly?: boolean;
  /**
   * Ignores password managers from suggesting passwords (e.g. 1Password).
   */
  shouldIgnorePasswordManagers?: boolean;
  /**
   * Suffix content can be rendered in after of the input field. This is useful to provide extra functionality,
   * such as a unit selector to create a `UnitInput`.
   */
  suffix?: React.ReactNode | React.ComponentType;
  /**
   * The input type.
   */
  type?: TextInputType;
  /**
   * The value of the form control. Should be coordinated with state using the `onChange` callback.
   */
  value?: string | number;
  /**
   * Renders a clear button when there is a value present. Use with the `onClear` prop.
   * @default false
   */
  isClearable?: boolean;
  /**
   * Applies the branded visual style (branded typography and increased padding).
   * @default false
   */
  isBranded?: boolean;
  /**
   * Callback function called when the clear button is clicked. Use with the `isClearable` prop.
   */
  onClear?: () => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'prefix'> &
  SpringTooling & {
    /**
     * An event handler for the onChange event
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void; //onChange is ordered after HTMLAttributes to resolve type merging;
  };

/**
 * Disables password managers from suggesting passwords.
 *
 * @see https://webflow.atlassian.net/browse/DS-1351
 */
const enhancePropsWithPasswordManagement = (
  props: TextInputProps,
  shouldDisable: boolean
) => {
  if (!shouldDisable) {
    return props;
  }

  return {
    ...props,
    ...propsToIgnorePasswordManagers,
  };
};

/**
 * # TextInput
 *
 * The TextInput component allow users to enter text data, with types including text, email, password, and more.
 *
 * ## Usage
 *
 * ```jsx
 * import {TextInput} from '@packages/systems/spring';
 *
 * function Example() {
 *   const [value, setValue] = React.useState('The world is your garden...');
 *   const handleOnChange = event => setValue(event.target.value);
 *
 *   return <TextInput value={value} onChange={handleOnChange} />;
 * }
 * ```
 *
 * @component
 */
export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      // Never render children for TextInput.
      // eslint-disable-next-line unused-imports/no-unused-vars
      children,
      className,
      defaultValue,
      isBranded = false,
      isClearable = false,
      isDisabled = false,
      id: idProp,
      isError: errorProp,
      isRequired: isRequiredProp,
      onBlur,
      onChange = () => undefined,
      onClear,
      onFocus,
      prefix,
      shouldIgnorePasswordManagers = false,
      style,
      suffix,
      value,
      ...rest
    }: TextInputProps,
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const id = useFormGroupId(idProp);
    const {
      ariaDescribedBy,
      isError: errorState,
      isRequired: isRequiredStateFromFormGroup,
    } = useFormGroupState();

    const [focused, setFocused] = React.useState(false);

    const isError = typeof errorProp === 'undefined' ? errorState : errorProp;
    const isRequired = getDefinedValue(
      isRequiredStateFromFormGroup,
      isRequiredProp
    );

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      if (
        event.relatedTarget instanceof Element &&
        event.relatedTarget?.closest(`[data-text-input-id="${id}"]`)
      ) {
        return;
      }

      setFocused(false);
      onBlur && onBlur(event);
    };

    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus && onFocus(event);
    };

    const handleClear = () => {
      onClear?.();

      // Focus the input after clearing
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
        setFocused(true);
      }
    };

    const prefixMarkup = prefix ? (
      <TextInputPrefixSuffixView isBranded={isBranded}>
        {typeof prefix === 'string' ? prefix : renderElement(prefix)}
      </TextInputPrefixSuffixView>
    ) : null;

    const suffixMarkup = suffix ? (
      <TextInputPrefixSuffixView isBranded={isBranded}>
        {typeof suffix === 'string' ? suffix : renderElement(suffix)}
      </TextInputPrefixSuffixView>
    ) : null;

    // Show clear button when isClearable is true and there's a value
    const shouldShowClearButton =
      isClearable && value && String(value).length > 0;

    const clearButtonMarkup = shouldShowClearButton ? (
      <TextInputClearButtonView
        aria-hidden
        Icon={CloseCircleIcon}
        isDisabled={isDisabled}
        label="Clear input"
        onClick={handleClear}
        tabIndex={-1}
        variant="ghost"
      />
    ) : null;

    const inputProps = enhancePropsWithPasswordManagement(
      rest,
      shouldIgnorePasswordManagers
    );

    const mergedRefs = useMergeRefs([ref, inputRef]);

    return (
      <TextInputWrapperView
        className={className}
        data-disabled={isDisabled}
        data-error={isError}
        data-focused={focused}
        data-text-input-id={id}
        isBranded={isBranded}
        style={style}
      >
        <TextInputContentView>
          {prefixMarkup}
          {/* @ts-expect-error - TS2322 - @SPRING_CLEANING: Types of property 'prefix' are incompatible. We've solved this in many other places by omitting `prefix` from the `HTMLAttributes` type, but that isn't working here. */}
          <TextInputView
            aria-describedby={ariaDescribedBy}
            defaultValue={defaultValue}
            disabled={isDisabled}
            id={id}
            isBranded={isBranded}
            onBlur={handleOnBlur}
            onChange={onChange}
            onFocus={handleOnFocus}
            ref={mergedRefs}
            required={isRequired}
            value={value}
            {...inputProps}
          />
          {suffixMarkup}
          {clearButtonMarkup}
        </TextInputContentView>
      </TextInputWrapperView>
    );
  }
);

TextInput.displayName = 'TextInput';
TextInput.toString = TextInputView.toString;

export const propsToIgnorePasswordManagers = {
  /**
   * 1Password.
   * @see https://developer.1password.com/docs/web/compatible-website-design/
   */
  'data-1p-ignore': true,
  /**
   * LastPass.
   * @see https://support.lastpass.com/s/document-item?bundleId=lastpass&topicId=LastPass/c_lp_prevent_fields_from_being_filled_automatically.html&_LANG=enus
   */
  'data-lpignore': true,
};

export default TextInput;
